import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useToast } from "vue-toastification";
import { getClassMyActivityList, getClassPersonalProfile, getMyEventList, getPersonalBasicInfo, isClassSession, normalizeAvatarUrl } from "@/utils/api";
import { getPersonalInfo } from "@/utils/mobileapi";
import { encrypt } from "@/utils/des";
import { formatTime } from "@/utils/utils";
import { safeList } from "@/utils/http";
const readUserData = () => {
  try {
    return JSON.parse(localStorage.getItem("userData") || "null");
  } catch (error) {
    console.error(error);
    return null;
  }
};
const displayValue = value => value === undefined || value === null || value === "" ? "—" : value;
const QR_REFRESH_SECONDS = 10;
export default {
  setup() {
    const toast = useToast();
    const userProfile = ref(null);
    const avatarLoadFailed = ref(false);
    const loading = ref(false);
    const eventLoading = ref(false);
    const myQRVisible = ref(false);
    const myEventVisible = ref(false);
    const qrcodeValue = ref("");
    const countdown = ref(QR_REFRESH_SECONDS);
    const eventList = ref([]);
    const currentPage = ref(1);
    const pageSize = ref(5);
    const totalItems = ref(0);
    let countdownInterval = null;
    const activeUserData = computed(() => readUserData());
    const isClassUser = computed(() => isClassSession(activeUserData.value));
    const platformLabel = computed(() => isClassUser.value ? "CLASS PLATFORM" : "LEGACY WEB");
    const displayName = computed(() => displayValue(userProfile.value?.realname));
    const initials = computed(() => String(displayName.value || "PU").trim().slice(0, 1).toUpperCase());
    const avatarUrl = computed(() => {
      const profile = userProfile.value || {};
      const userData = activeUserData.value || {};
      return normalizeAvatarUrl(profile.avatar, profile.avatarUrl, profile.avatorName, profile.user_info?.avatar, userData.classUserInfo?.avatorName, userData.classUserInfo?.avatar, userData.baseUserInfo?.avatar, userData.user_info?.avatar);
    });
    const avatarVisible = computed(() => Boolean(avatarUrl.value) && !avatarLoadFailed.value);
    const hasNextPage = computed(() => {
      if (totalItems.value) return currentPage.value * pageSize.value < totalItems.value;
      return eventList.value.length >= pageSize.value;
    });
    const metricCards = computed(() => [{
      label: "二课分",
      value: displayValue(userProfile.value?.credit),
      hint: "Second classroom"
    }, {
      label: "诚信度",
      value: displayValue(userProfile.value?.cx),
      hint: "Credit standing"
    }, {
      label: "PU银豆",
      value: displayValue(userProfile.value?.amount2),
      hint: "Campus balance"
    }, {
      label: "活动 / 群组",
      value: `${displayValue(userProfile.value?.event_count)} / ${displayValue(userProfile.value?.group_count)}`,
      hint: "Participation"
    }]);
    const profileFields = computed(() => [{
      label: "姓名",
      value: displayName.value
    }, {
      label: "学号",
      value: displayValue(userProfile.value?.sno)
    }, {
      label: "学院",
      value: displayValue(userProfile.value?.yx)
    }, {
      label: "专业",
      value: displayValue(userProfile.value?.major)
    }, {
      label: "年级",
      value: displayValue(userProfile.value?.year)
    }, {
      label: "班级",
      value: displayValue(userProfile.value?.class)
    }]);
    const goLogin = () => {
      window.location.hash = "#/login";
    };
    const ensureLoggedIn = () => {
      const loggedIn = localStorage.getItem("loggedIn");
      const userData = readUserData();
      const valid = loggedIn === "true" && userData && (isClassSession(userData) || userData.oauth_token && userData.oauth_token_secret);
      if (!valid) {
        toast.error("未登录或 token 失效，请重新登录", {
          position: "top-center"
        });
        goLogin();
        return null;
      }
      return userData;
    };
    const fetchData = async () => {
      const userData = ensureLoggedIn();
      if (!userData) return;
      loading.value = true;
      try {
        if (isClassSession(userData)) {
          const response = await getClassPersonalProfile(userData);
          userProfile.value = response.content;
          avatarLoadFailed.value = false;
          return;
        }
        const response = await getPersonalBasicInfo(userData.oauth_token, userData.oauth_token_secret);
        if (response.code === 0 && response.message === "success") {
          userProfile.value = response.content;
          avatarLoadFailed.value = false;
        } else {
          toast.error("获取个人信息失败", {
            position: "top-center"
          });
        }
      } catch (error) {
        if (error?.data?.message === "授权失败" || error?.response?.data?.message === "授权失败") {
          toast.error("未登录或 token 失效，请重新登录", {
            position: "top-center"
          });
          goLogin();
        } else {
          toast.error("获取个人信息失败，请检查网络连接。", {
            position: "top-center"
          });
          console.error(error);
        }
      } finally {
        loading.value = false;
      }
    };
    const getMyEvent = async () => {
      const userData = ensureLoggedIn();
      if (!userData) return;
      eventLoading.value = true;
      try {
        if (isClassSession(userData)) {
          const response = await getClassMyActivityList(userData, currentPage.value, pageSize.value, 1);
          const list = safeList(response);
          eventList.value = list;
          totalItems.value = response?.total_count || list.length;
          myEventVisible.value = true;
          return;
        }
        const response = await getMyEventList(userData.oauth_token, userData.oauth_token_secret, currentPage.value, pageSize.value);
        const list = safeList(response);
        eventList.value = list;
        totalItems.value = response?.total_count || response?.content?.total_count || list.length;
        myEventVisible.value = true;
      } catch (error) {
        if (error?.data?.message === "授权失败" || error?.response?.data?.message === "授权失败") {
          toast.error("未登录或 token 失效，请重新登录", {
            position: "top-center"
          });
          goLogin();
        } else {
          toast.error("获取活动列表失败，请检查网络连接。", {
            position: "top-center"
          });
          console.error(error);
        }
      } finally {
        eventLoading.value = false;
      }
    };
    const clearCountdown = () => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
      }
    };
    const startCountdown = () => {
      clearCountdown();
      countdownInterval = setInterval(() => {
        if (!myQRVisible.value) {
          clearCountdown();
          return;
        }
        if (countdown.value > 0) {
          countdown.value -= 1;
        } else {
          generateQRCode();
        }
      }, 1000);
    };
    const generateQRCode = async () => {
      const userData = ensureLoggedIn();
      if (!userData) return;
      try {
        let uid;
        let username;
        if (isClassSession(userData)) {
          const identity = userData.classUserInfo || userData.baseUserInfo || {};
          uid = identity.uid || identity.id || userData.user_info?.uid;
          username = identity.username || identity.uname || userData.user_info?.uname;
        } else {
          const personalInfo = await getPersonalInfo(userData.oauth_token, userData.oauth_token_secret);
          uid = userData.user_info?.uid;
          username = personalInfo.content?.user_info?.uname;
        }
        if (!uid || !username) throw new Error("二维码身份信息不完整");
        const time = Math.floor(Date.now() / 1000);
        qrcodeValue.value = encrypt(`xyhui://user/${uid}/${time}/${username}`);
        myQRVisible.value = true;
        countdown.value = QR_REFRESH_SECONDS;
        startCountdown();
      } catch (error) {
        toast.error("生成二维码失败。", {
          position: "top-center"
        });
        console.error(error);
      }
    };
    const logout = () => {
      localStorage.removeItem("loggedIn");
      window.dispatchEvent(new Event("pu-auth-changed"));
      localStorage.removeItem("userData");
      clearCountdown();
      toast.success("登出成功", {
        position: "top-center"
      });
      goLogin();
    };
    const handleAvatarError = () => {
      avatarLoadFailed.value = true;
    };
    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value -= 1;
        getMyEvent();
      }
    };
    const nextPage = () => {
      if (!hasNextPage.value) return;
      currentPage.value += 1;
      getMyEvent();
    };
    watch(myQRVisible, visible => {
      if (!visible) clearCountdown();
    });
    onMounted(fetchData);
    onUnmounted(clearCountdown);
    return {
      userProfile,
      loading,
      eventLoading,
      logout,
      myQRVisible,
      generateQRCode,
      qrcodeValue,
      countdown,
      myEventVisible,
      eventList,
      getMyEvent,
      currentPage,
      prevPage,
      nextPage,
      hasNextPage,
      formatTime,
      metricCards,
      profileFields,
      displayName,
      initials,
      avatarUrl,
      avatarVisible,
      handleAvatarError,
      platformLabel,
      goLogin
    };
  }
};